import { type ComponentProps, forwardRef } from 'react';
import { StyleSheet, type TextInput, View } from 'react-native';
import { MaskedTextInput, type MaskedTextInputProps } from 'react-native-mask-text';

import { theme, TYPE_CONFIG } from '@garnish/constants';

import { useUniqueNativeID } from '../../hooks';
import { webOnlyStyles } from '../../utils';
import { VStack } from '../Stack';
import { TagAlert } from '../TagAlert';

// ─────────────────────────────────────────────────────────────────────────────

const MaskedTextFieldRaw = forwardRef<TextInput, Props>((props, ref) => {
  const { style, notice, noticePalette, invalid, ...restProps } = props;

  const inputStyles = [styles.input, webStyles, style];

  const noticeId = useUniqueNativeID('text-field-banner');

  return (
    <VStack gap={theme.spacing['2']}>
      <MaskedTextInput
        ref={ref}
        style={inputStyles}
        placeholderTextColor={theme.colors.CHARCOAL}
        {...{ 'aria-invalid': invalid }}
        {...(invalid && notice ? { 'aria-errormessage': noticeId } : {})}
        {...restProps}
      />

      {notice ? (
        <View nativeID={noticeId}>
          <TagAlert palette={noticePalette}>{notice}</TagAlert>
        </View>
      ) : null}
    </VStack>
  );
});

const MaskedTextFieldCurrency = forwardRef<TextInput, Props>((props, ref) => {
  return (
    <MaskedTextFieldRaw
      ref={ref}
      placeholderTextColor={theme.colors.CHARCOAL}
      type="currency"
      options={CURRENCY_OPTIONS}
      keyboardType="number-pad"
      {...props}
    />
  );
});

const MaskedTextFieldPhoneNumber = forwardRef<TextInput, Props>((props, ref) => {
  return (
    <MaskedTextFieldRaw
      ref={ref}
      placeholderTextColor={theme.colors.CHARCOAL}
      type="custom"
      mask="(999)999-9999"
      keyboardType="number-pad"
      {...props}
    />
  );
});

const MaskedTextFieldDate = forwardRef<TextInput, Props>((props, ref) => {
  return (
    <MaskedTextFieldRaw
      ref={ref}
      placeholderTextColor={theme.colors.CHARCOAL}
      type="custom"
      mask="99/99/9999"
      keyboardType="number-pad"
      {...props}
    />
  );
});

// ─────────────────────────────────────────────────────────────────────────────

export const MaskedTextField = {
  Raw: MaskedTextFieldRaw,
  Currency: MaskedTextFieldCurrency,
  PhoneNumber: MaskedTextFieldPhoneNumber,
  Date: MaskedTextFieldDate,
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  input: {
    ...TYPE_CONFIG.BODY['16'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    color: theme.colors.TEXT_COLOR,
    borderRadius: theme.radius.medium,
    paddingVertical: theme.spacing['4'],
    paddingHorizontal: theme.spacing['3'],
    borderBottomWidth: 0,
  },
});

const webStyles = webOnlyStyles({
  outlineColor: theme.colors.GRAY,
});

// ─── Constants ───────────────────────────────────────────────────────────────

const CURRENCY_OPTIONS: MaskedTextInputProps['options'] = {
  prefix: '$',
  decimalSeparator: '.',
  groupSeparator: ',',
  precision: 2,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = MaskedTextInputProps & {
  invalid?: boolean;
  notice?: string;
  noticePalette?: ComponentProps<typeof TagAlert>['palette'];
};
